import React, { useMemo } from 'react'
import { SprintUserStory } from '@jirax/common'
import {
  Chart as ChartJS,
  TimeScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
  Filler
} from 'chart.js'
import { Line } from 'react-chartjs-2'
import 'chartjs-adapter-luxon'

ChartJS.register(TimeScale, LinearScale, PointElement, LineElement, Tooltip, Legend, Filler)

export const ActivityChart: React.FC<{ userStory: SprintUserStory }> = ({ userStory }) => {
  const chartData = useMemo(() => {
    const events: { date: string; type: string }[] = []

    userStory.ticketMovements.forEach(m => {
      events.push({ date: m.changedAt, type: 'movement' })
    })
    userStory.commentActivity.forEach(a => {
      (a.commentDates ?? []).forEach(date => {
        events.push({ date, type: 'comment' })
      })
    })
    userStory.scopeChanges.forEach(s => {
      events.push({ date: s.changedAt, type: s.type === 'added' ? 'scope-add' : 'scope-remove' })
    })

    if (events.length === 0) return null

    const dayMap = new Map<string, { movement: number; comment: number; scope: number }>()
    events.forEach(({ date, type }) => {
      const day = new Date(date).toISOString().slice(0, 10)
      if (!dayMap.has(day)) dayMap.set(day, { movement: 0, comment: 0, scope: 0 })
      const bucket = dayMap.get(day)!
      if (type === 'movement') bucket.movement++
      else if (type === 'comment') bucket.comment++
      else bucket.scope++
    })

    const days = Array.from(dayMap.keys()).sort()

    return {
      labels: days,
      datasets: [
        {
          label: 'Ticket Movements',
          data: days.map(d => dayMap.get(d)!.movement),
          borderColor: 'rgb(99, 102, 241)',
          backgroundColor: 'rgba(99, 102, 241, 0.1)',
          fill: true,
          tension: 0.3,
          pointRadius: 3
        },
        {
          label: 'Comments',
          data: days.map(d => dayMap.get(d)!.comment),
          borderColor: 'rgb(168, 85, 247)',
          backgroundColor: 'rgba(168, 85, 247, 0.1)',
          fill: true,
          tension: 0.3,
          pointRadius: 3
        },
        {
          label: 'Scope Changes',
          data: days.map(d => dayMap.get(d)!.scope),
          borderColor: 'rgb(249, 115, 22)',
          backgroundColor: 'rgba(249, 115, 22, 0.1)',
          fill: true,
          tension: 0.3,
          pointRadius: 3
        }
      ]
    }
  }, [userStory])

  if (!chartData) return null

  return (
    <div className='mt-4 h-40'>
      <Line
        data={chartData}
        options={{
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: { position: 'top' as const, labels: { boxWidth: 12, font: { size: 11 } } },
            tooltip: { mode: 'index' as const, intersect: false }
          },
          scales: {
            x: {
              type: 'category' as const,
              ticks: { font: { size: 10 }, maxRotation: 0 },
              grid: { display: false }
            },
            y: {
              beginAtZero: true,
              ticks: { stepSize: 1, font: { size: 10 } },
              grid: { color: 'rgba(0,0,0,0.05)' }
            }
          }
        }}
      />
    </div>
  )
}